import React, { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Flex, ToastId, useBoolean } from "@chakra-ui/react";
import { langStore, useSelecting } from "@noli/business";

import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import useImageWithPlaceholder from "@/modules/editor/hooks/useImageWithPlaceholder";
import { MattEditorModal } from "@/modules/editor/components/MattEditor";
import {
  getBatchCutoutJobResult,
  // getJobResult,
  postBatchCutoutImage,
  // postCutoutImageUrl,
} from "@/api/file";
import { MattMeta, MetaType } from "@/types/meta";
import { Actions } from "@/utils/api";
import { useOwnToast } from "@/utils/misc";
import { tasksStore } from "@/stores/tasks";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import { Dictionary } from "@/types/base";
import { addVirtualNode, recoverVirtualNode } from "@/utils/safeOp";
import { InfoText } from "@/modules/editor/components/styleComponents/Texts";
import { GeneratePriceRules, useGenerateCost } from "@/modules/editor/hooks/useGenerateCost";
import { isAuthUser } from "@/utils/status";
import { userAssetsStore } from "@/stores/userAssets";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
// import { MODEL_TYPES_INFO } from "@/modules/editor/constant";

const SOD_BUSY_ALIASES = ".SOD.";
const IMattPanel: React.FC = () => {
  const toast = useOwnToast();
  const { translate } = useTranslate();
  // const [modalOpen, modalOp] = useBoolean(false);
  const [loading, loadingOp] = useBoolean(false);
  // const [cutUrl, setCutUrl] = useState<string>();

  const toastIdRef = useRef<ToastId | null>(null);
  const {
    url,
    placeholder,
    enable,
    meta: originMeta,
  } = useImageWithPlaceholder({
    type: "image",
    placeholderConfig: {
      notMatchCondition: Editor_Words.current_select_is_not_origin_image,
      noneSelect: Editor_Words.select_an_image_uncut,
    },
    condition: (type, displayNode) => {
      // type 为非抠图的图
      return displayNode?.type === "image" && displayNode.params?.meta?.type !== "SOD";
    },
  });
  const nodeInfo = useSelecting("basic")({ fixed: 0 });
  const virtualAliasRef = useRef<string | null>(null);

  const cleanupSetup = () => {
    tasksStore.removeBusyNode(SOD_BUSY_ALIASES);
    if (virtualAliasRef.current) {
      recoverVirtualNode({ alias: virtualAliasRef.current });
    }
    closeCutoutingToast();
  };

  const closeCutoutingToast = () => {
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
    }
  };

  const runLoopGetUploadFileResult = (job_id: string) => {
    console.log("runLoopGetUploadFileResult", job_id);
    let _timer: NodeJS.Timer | null = null;

    function cancel() {
      if (_timer) {
        clearTimeout(_timer);
      }
    }

    function clear() {
      cancel();
      loadingOp.off();
      cleanupSetup();
    }

    function errorHandler(errMsg?: string | null) {
      if (errMsg) {
        toast({
          title: errMsg,
          status: "error",
        });
      }
      clear();
    }

    async function successHandler(
      data: {
        images: Dictionary<string>;
        image_result: Dictionary<{
          image_url: string;
          job_id: string;
          reason: string;
          success: boolean;
        }>;
      } | null,
    ) {
      const { image_result = {} } = data ?? {};
      const result = image_result?.[job_id];
      const image_url = result?.success ? result.image_url : null;

      if (result && !result.success) {
        errorHandler(result.reason);
        return;
      }

      if (image_url) {
        // setCutUrl(image_url);

        const meta: MattMeta = {
          type: "SOD",
          data: {
            originUrl: url!,
            url: image_url,
            cutoutUrl: image_url,
            wh: nodeInfo!.imgWH!,
            from: (originMeta as MetaType) ?? undefined,
          },
        };
        await Actions.importMeta({
          t: toast,
          lang: langStore.tgt,
          type: "SOD",
          meta,
          setupInfo: {
            checkBusy: false,
            busyAliases: [SOD_BUSY_ALIASES],
          },
          opt: null,
        });
        clear();
        if (isAuthUser()) userAssetsStore.fetchHeartCoin();
      } else {
        _timer = setTimeout(() => {
          run();
        }, 1000);
      }
    }

    async function run() {
      if (_timer) {
        clearTimeout(_timer);
      }
      try {
        const res = await getBatchCutoutJobResult([job_id]);
        if (res.code === 0 && res?.data) {
          successHandler(res.data);
        } else if (res.code !== 0) {
          errorHandler();
        }
      } catch (e) {
        errorHandler();
      }
    }

    run();
  };

  async function handleCutout() {
    if (!url) return;
    if (!nodeInfo?.imgWH) return;

    loadingOp.on();
    toastIdRef.current = toast({
      status: "success",
      title: translate(Editor_Words.cutouting_message),
      duration: null,
    });
    tasksStore.addBusyNode(SOD_BUSY_ALIASES);
    virtualAliasRef.current = await addVirtualNode(toast, "/cutout", langStore.tgt, {
      w: nodeInfo.imgWH.w,
      h: nodeInfo.imgWH.h,
    });
    postBatchCutoutImage([url])
      .then((res) => res)
      .then((innerRes) => {
        if (innerRes.code === 0) {
          const jobResults = innerRes.data?.job_result;
          const job_id = innerRes.data?.job_result ? jobResults?.[url]?.job_id : null;

          if (!jobResults?.[url]?.success || !job_id) {
            toast({
              status: "error",
              title: !jobResults?.[url]?.reason ?? translate(Editor_Words.cutout_failed_message),
            });
            return Promise.reject(!jobResults?.[url].reason);
          }
          runLoopGetUploadFileResult(job_id);
        } else {
          return Promise.reject(innerRes);
        }
      })
      .catch((err) => {
        console.error("err: ", err);
        loadingOp.off();
        cleanupSetup();
      });
  }

  const revolutionString = useMemo(() => {
    return nodeInfo?.imgWH
      ? `${nodeInfo.imgWH.w} × ${nodeInfo.imgWH.h} px`
      : translate(Editor_Words["general-loading"]);
  }, [nodeInfo?.imgWH]);

  return (
    <SubWithTitle title={translate(Editor_Words.cutout_title)}>
      <ImageWithPlaceHolder url={url} placeholderText={placeholder} />
      {enable ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          <InfoText>
            {translate(Editor_Words.model)}：{translate(Editor_Words.nolibox_matt_model)}
          </InfoText>
          <InfoText>
            {translate(Editor_Words["resolution"])}： {revolutionString}
          </InfoText>
        </Flex>
      ) : null}

      <BizGenerateButton
        mt="24px"
        pb="32px"
        onSubmit={handleCutout}
        buttonProps={{
          isDisabled: !enable,
        }}
        checkBusyConfig={{
          checkAll: true,
          busyAlias: SOD_BUSY_ALIASES,
        }}
        sticky
        calcPriceProps={GeneratePriceRules.Matt}
      >
        {translate(Editor_Words.cutout_button_text)}
      </BizGenerateButton>
      {/* {url && cutUrl && (
        <MattEditorModal
          open={modalOpen}
          onClose={() => modalOp.off()}
          originUrl={url ?? ""}
          cutUrl={cutUrl ?? ""}
          onConfirm={(url) => console.log(url)}
        />
      )} */}
    </SubWithTitle>
  );
};

export const MattPanel = observer(IMattPanel);
